import { FC } from 'react';
import toast from 'react-hot-toast';

import {
  listCompanyUsers,
  updateCompanyUserRole,
} from '@sdk/managers/UsersManager';
import { useAuth } from '@hooks/Auth';
import useSwr, { mutate } from 'swr';

import Toggle from '@components/elements/Toggle';
import { CompanyRole } from '@sdk/API';
import RoleBadge from '@components/elements/RoleBadge';

const CompanyProxyList: FC = () => {
  const { dbUser, company, loading } = useAuth();

  const { data: members } = useSwr(
    company?.companyID ? ['/admin/members', company?.companyID] : null,
    async () => {
      if (!company) {
        if (loading === false) throw Error('User does not own any company.');
        return;
      }

      const proxies: any = await listCompanyUsers(company.companyID);
      return proxies.data?.result?.items || [];
    },
    {
      revalidateOnFocus: false,
      onError: (err, key) => {
        console.log('Failed to fetch proxies', { key }, err);
        toast.error(err?.message || err?.errors?.[0]?.message);
      },
    }
  );

  const removeProxy = async (roleId: string) => {
    try {
      if (!dbUser?.id || !dbUser?.username)
        throw new Error('Unexpected error.');

      if (!company) throw new Error('Failed to get company.');

      console.log('unassignProxy', roleId);
      await toast.promise(updateCompanyUserRole(roleId, CompanyRole.EMPLOYEE), {
        loading: 'Removing proxy...',
        success: 'Successfully removed proxy',
        error: 'Failed to remove proxy',
      });

      await Promise.all([mutate(['/admin/members', company?.companyID])]);
      // console.log('unassignProxy res', res);
    } catch (err: any) {
      console.log('Failed unassignProxy', err);
    }
  };

  const addProxy = async (roleId: string) => {
    try {
      if (!dbUser?.id || !dbUser?.username || !company) {
        throw new Error('Unexpected error.');
      }

      console.log('assignProxy', roleId);
      await toast.promise(updateCompanyUserRole(roleId, CompanyRole.PROXY), {
        loading: 'Adding proxy...',
        success: 'Successfully added proxy',
        error: 'Failed to add proxy',
      });

      await Promise.all([mutate(['/admin/members', company?.companyID])]);
      // console.log('assignProxy res', res);
    } catch (err: any) {
      console.log('Failed assignProxy', err);
    }
  };

  const onProxyToggle = async (value: boolean, role: any) => {
    if (value) {
      addProxy(role.id);
    } else {
      removeProxy(role.id);
    }
  };

  const filteredMembers = members?.filter(
    (member) =>
      [CompanyRole.ADMIN, CompanyRole.REPRESENTATIVE].includes(member.role) ===
      false
  );

  return (
    <>
      <div className="flex flex-col divide-opacity-30 divide-y-1 divide-solid ">
        {members && members.length === 0 && <div>No members in company</div>}
        {filteredMembers?.map((member) => (
          <div
            key={`member-${member.id}`}
            className="flex items-center justify-between py-2 "
          >
            <div className="text-left">
              <div>
                <RoleBadge role={member.role} />
              </div>
              <div>
                {member.user.firstName || member.user.lastName
                  ? `
                  ${member.user.firstName}
                  ${member.user.lastName && ` ${member.user.lastName}`}
                  ${member.user.username && ` (${member.user.username})`}
                `
                  : member.user.email}
              </div>
            </div>
            <div>
              <Toggle
                value={member.role === CompanyRole.PROXY}
                onChange={(v) => {
                  console.log(v);
                  onProxyToggle(v, member);
                }}
                activeClassName="bg-magenta"
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default CompanyProxyList;
